import { useEffect, useMemo } from 'react';
import { Check, Edit } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Paper, PaperProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserInput, useUserQuery } from '../../../api/gql';
import { FullPageLoader } from '../../../components';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { UserForm, UserFormType } from '../../../modules/forms/user/UserForm';
import { notify } from '../../../utils/notification';
import { useUrlParam } from '../../../utils/urlParams';
import { useUpdateUser } from '../../user-registration/hooks/useUdateUser';

const TRANS_NS = 'page-user-profile';

export const UserEditCard = ({ sx, ...rest }: PaperProps) => {
  const { t } = useTranslation(TRANS_NS);
  const { isLoading, data, error } = useUserQuery();

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({
        message: t('error.query'),
      });
    }
  }, [error, t]);

  const { updateUser, isPending: isUpdating } = useUpdateUser({
    onSuccess: () => {
      clearEditParam();
      notify.positive({
        message: t('success'),
      });
    },
    onError: () => {
      notify.negative({
        message: t('error.mutation'),
      });
    },
  });
  const [editParam, setEditParam, clearEditParam] = useUrlParam('userEdit');

  const isEditingMode = useMemo(() => editParam() === 'true', [editParam]);

  const turnOnEditMode = () => {
    setEditParam('true');
  };
  const turnOffEditMode = () => {
    clearEditParam();
  };

  const onSubmit = (formData: UserFormType) => {
    const dto: UserInput = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      city: formData.city,
      streetAddress: formData.streetAddress,
      zipCode: formData.zipCode,
      country: formData.country,
      phoneNumber: formData.phoneNumber,
      businessName: formData.businessName,
      vatId: formData.vatId,
      taxId: formData.taxId,
      bankAccount: formData.bankAccount,
      bankNumber: formData.bankNumber,
      swift: formData.swift,
      iban: formData.iban,
    };
    updateUser({
      input: dto,
    });
  };

  if (isLoading) {
    return <FullPageLoader />;
  }
  return (
    <Paper sx={{ p: 4, ...sx }} {...rest}>
      {error ? <ExpandedBox>{t('error.query')}</ExpandedBox> : null}
      <UserForm
        onSubmit={onSubmit}
        readonly={!isEditingMode}
        values={data?.user}
        defaultValues={data?.user}
        displayAresBtn={isEditingMode}
        renderActions={({ isDirty }, reset) => (
          <Box display={'flex'} flexDirection={'row'} gap={4} mt={4} justifyContent={'flex-end'}>
            {isEditingMode ? (
              <LoadingButton
                loading={isUpdating}
                size={'large'}
                disabled={!isDirty}
                variant={'contained'}
                startIcon={<Check />}
                type={'submit'}
                fullWidth
              >
                {t('action.mutate')}
              </LoadingButton>
            ) : null}
            {isEditingMode ? (
              <Button
                size={'large'}
                onClick={() => {
                  turnOffEditMode();
                  reset();
                }}
              >
                {t('action.cancel')}
              </Button>
            ) : (
              <Button size={'large'} variant={'outlined'} startIcon={<Edit />} onClick={turnOnEditMode}>
                {t('action.edit')}
              </Button>
            )}
          </Box>
        )}
      />
    </Paper>
  );
};
